import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, Plus, Search, AlertTriangle, CheckCircle2, X, Building2, Sparkles,
    Languages, Copy, Tag,
} from "lucide-react";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import TagsInput from "./TagsInput";
import { categoriesApi } from "@/lib/api/categories";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

const NAME_MAX = 80;
const EXPL_MAX = 200;

const EMPTY = {
    level: 1,
    parent_ids: [],
    name_en: "",
    name_ar: "",
    explanatory_name_en: "",
    explanatory_name_ar: "",
    tags_en: [],
    tags_ar: [],
    is_active: true,
};

function deepEq(a, b) { return JSON.stringify(a) === JSON.stringify(b); }

/* ------------------------------------------------------------------ */
/*  Right-side edit panel — replaces CategoryDialog modal.            */
/*  Phases 1–4 of the audit, all in one component.                    */
/* ------------------------------------------------------------------ */

export default function CategoryEditPanel({
    open,
    onOpenChange,
    mode,                  // "create" | "edit"
    initial,               // node (or { level, parent_ids } for create)
    tree,                  // full tree (for parent picker + tag autocomplete)
    usage,                 // { [id]: { providers, offers } }
    onSaved,               // (saved, opts?) => void
}) {
    const { t, lang, isRTL } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [original, setOriginal] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [submitMode, setSubmitMode] = useState("save"); // save | save_add_another | save_add_l2
    const [error, setError] = useState("");
    const [parentSearch, setParentSearch] = useState("");
    const [touched, setTouched] = useState({});
    const [duplicateName, setDuplicateName] = useState(false);
    const firstInputRef = useRef(null);

    const isEdit = mode === "edit";
    const isL2 = form.level === 2;

    /* ---------- Init / reset ---------- */
    useEffect(() => {
        if (!open) return;
        const seed = initial ? {
            level: initial.level,
            parent_ids: initial.parent_ids || [],
            name_en: initial.name_en || "",
            name_ar: initial.name_ar || "",
            explanatory_name_en: initial.explanatory_name_en || "",
            explanatory_name_ar: initial.explanatory_name_ar || "",
            tags_en: initial.tags_en || [],
            tags_ar: initial.tags_ar || [],
            is_active: initial.is_active !== false,
        } : { ...EMPTY };
        setForm(seed);
        setOriginal(seed);
        setError("");
        setTouched({});
        setDuplicateName(false);
        setParentSearch("");
        // Auto-focus first input shortly after the sheet animates in
        setTimeout(() => firstInputRef.current?.focus(), 60);
    }, [open, initial]);

    const dirty = !deepEq(form, original);

    /* ---------- Live duplicate-name check ---------- */
    useEffect(() => {
        const en = form.name_en.trim().toLowerCase();
        if (!en) { setDuplicateName(false); return; }
        const all = [];
        (tree || []).forEach((l1) => {
            all.push(l1);
            (l1.children || []).forEach((c) => all.push(c));
        });
        const dup = all.some((c) => c.id !== initial?.id && (c.name_en || "").toLowerCase() === en);
        setDuplicateName(dup);
    }, [form.name_en, tree, initial?.id]);

    /* ---------- Tag autocomplete suggestions ---------- */
    const allTagsEn = useMemo(() => {
        const set = new Set();
        (tree || []).forEach((l1) => {
            (l1.tags_en || []).forEach((tg) => set.add(tg));
            (l1.children || []).forEach((c) => (c.tags_en || []).forEach((tg) => set.add(tg)));
        });
        return Array.from(set).sort();
    }, [tree]);
    const allTagsAr = useMemo(() => {
        const set = new Set();
        (tree || []).forEach((l1) => {
            (l1.tags_ar || []).forEach((tg) => set.add(tg));
            (l1.children || []).forEach((c) => (c.tags_ar || []).forEach((tg) => set.add(tg)));
        });
        return Array.from(set).sort();
    }, [tree]);

    /* ---------- Parent picker data (search + child counts + status) ---------- */
    const filteredParents = useMemo(() => {
        const list = (tree || []).map((l1) => ({
            ...l1,
            childCount: (l1.children || []).length,
        }));
        const q = parentSearch.trim().toLowerCase();
        if (!q) return list;
        return list.filter((l) =>
            (l.name_en || "").toLowerCase().includes(q) ||
            (l.name_ar || "").toLowerCase().includes(q)
        );
    }, [tree, parentSearch]);

    /* ---------- Helpers ---------- */
    const update = (patch) => setForm((p) => ({ ...p, ...patch }));
    const markTouched = (k) => setTouched((p) => ({ ...p, [k]: true }));
    const toggleParent = (id) => {
        setForm((p) => ({
            ...p,
            parent_ids: p.parent_ids.includes(id)
                ? p.parent_ids.filter((x) => x !== id)
                : [...p.parent_ids, id],
        }));
    };

    /* ---------- Validation (live + on submit) ---------- */
    const validation = useMemo(() => {
        const errs = {};
        if (!form.name_en.trim()) errs.name_en = t("categories.errors.nameEnRequired");
        else if (form.name_en.length > NAME_MAX) errs.name_en = `Max ${NAME_MAX} characters.`;
        else if (duplicateName) errs.name_en = t("categories.errors.nameEnDuplicate");
        if (!form.name_ar.trim()) errs.name_ar = t("categories.errors.nameArRequired");
        else if (form.name_ar.length > NAME_MAX) errs.name_ar = `Max ${NAME_MAX} characters.`;
        if (form.explanatory_name_en && form.explanatory_name_en.length > EXPL_MAX) {
            errs.explanatory_name_en = `Max ${EXPL_MAX} characters.`;
        }
        if (form.explanatory_name_ar && form.explanatory_name_ar.length > EXPL_MAX) {
            errs.explanatory_name_ar = `Max ${EXPL_MAX} characters.`;
        }
        if (isL2 && form.parent_ids.length === 0) errs.parent_ids = t("categories.errors.parentRequired");
        return errs;
    }, [form, isL2, duplicateName, t]);

    const isValid = Object.keys(validation).length === 0;

    /* ---------- Translation completeness chip ---------- */
    const tx = {
        en: !!form.name_en.trim(),
        ar: !!form.name_ar.trim(),
    };

    /* ---------- Usage info (edit mode only) ---------- */
    const myUsage = isEdit && initial ? (usage || {})[initial.id] || { providers: 0, offers: 0 } : null;
    const willHide = isEdit && original.is_active && !form.is_active && (myUsage?.providers || myUsage?.offers);

    /* ---------- Submit ---------- */
    const submit = async (e, intent = "save") => {
        if (e?.preventDefault) e.preventDefault();
        setError("");
        setTouched({ name_en: true, name_ar: true, parent_ids: true, explanatory_name_ar: true });
        if (!isValid) return;
        setSubmitting(true);
        setSubmitMode(intent);
        try {
            const payload = {
                level: form.level,
                parent_ids: isL2 ? form.parent_ids : [],
                name_en: form.name_en.trim(),
                name_ar: form.name_ar.trim(),
                explanatory_name_en: form.explanatory_name_en?.trim() || null,
                explanatory_name_ar: form.explanatory_name_ar?.trim() || null,
                tags_en: form.tags_en,
                tags_ar: form.tags_ar,
                is_active: form.is_active,
            };
            const saved = isEdit
                ? await categoriesApi.update(initial.id, payload)
                : await categoriesApi.create(payload);
            toast.success(t(isEdit ? "categories.toasts.updated" : "categories.toasts.created"));
            onSaved?.(saved, { intent });
            if (intent === "save_add_another") {
                // Reset form for another L1 / L2 at the same level/parent
                const reset = {
                    ...EMPTY,
                    level: form.level,
                    parent_ids: form.parent_ids,
                };
                setForm(reset);
                setOriginal(reset);
                setTouched({});
                setTimeout(() => firstInputRef.current?.focus(), 60);
                return; // keep panel open
            }
            if (intent === "save_add_l2" && saved?.id) {
                // Switch the panel to "create L2 with this L1 as parent"
                const reset = { ...EMPTY, level: 2, parent_ids: [saved.id] };
                setForm(reset);
                setOriginal(reset);
                setTouched({});
                setTimeout(() => firstInputRef.current?.focus(), 60);
                return;
            }
            requestClose(true); // force close (already saved → no dirty guard)
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------- Unsaved guard ---------- */
    const requestClose = (force = false) => {
        if (!force && dirty) {
            if (!window.confirm(t("categories.unsavedConfirm"))) return;
        }
        onOpenChange(false);
    };

    /* ---------- Keyboard: Cmd/Ctrl+Enter to submit ---------- */
    const onKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit(null, "save");
        }
    };

    /* ---------- UI ---------- */
    const titleKey = isEdit ? "categories.editTitle" : (isL2 ? "categories.addL2Title" : "categories.addL1Title");
    const saveLabel = isEdit
        ? t("categories.saveChanges")
        : isL2 ? t("categories.createSubcategory") : t("categories.createParent");

    return (
        <Sheet open={open} onOpenChange={(o) => o ? onOpenChange(true) : requestClose()}>
            <SheetContent
                side={isRTL ? "left" : "right"}
                className="w-full sm:max-w-xl flex flex-col p-0"
                onKeyDown={onKeyDown}
                data-testid="category-edit-panel"
                dir={isRTL ? "rtl" : "ltr"}
            >
                <SheetHeader className="px-6 pt-6 pb-3 border-b border-border">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <SheetTitle>{t(titleKey)}</SheetTitle>
                            {isEdit && (
                                <Badge variant="outline" className="text-[10px]">
                                    {t(`categories.levels.l${form.level}`)}
                                </Badge>
                            )}
                        </div>
                        <TranslationChip ok_en={tx.en} ok_ar={tx.ar} t={t} />
                    </div>
                    <SheetDescription>{t("categories.dialogDescription")}</SheetDescription>

                    {/* Usage chip on edit */}
                    {isEdit && myUsage && (myUsage.providers > 0 || myUsage.offers > 0) && (
                        <div className="mt-2 flex items-center gap-2 text-xs">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary">
                                <Building2 className="h-3 w-3" /> {myUsage.providers} {t("categories.cols.providers")}
                            </span>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-secondary">
                                <Sparkles className="h-3 w-3" /> {myUsage.offers} {t("categories.cols.offers")}
                            </span>
                            {isEdit && initial?.id && (
                                <button
                                    type="button"
                                    className="ms-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                                    onClick={() => { navigator.clipboard.writeText(initial.id); toast.success("ID copied"); }}
                                >
                                    <Copy className="h-3 w-3" />
                                    <span className="font-mono">{(initial.id || "").slice(0, 8)}…</span>
                                </button>
                            )}
                        </div>
                    )}
                </SheetHeader>

                <form onSubmit={(e) => submit(e, "save")} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                    {/* Level — read-only on edit */}
                    {!isEdit && (
                        <div>
                            <Label>{t("categories.fields.level")}</Label>
                            <div className="mt-1.5 flex gap-2">
                                {[1, 2].map((lv) => (
                                    <button
                                        type="button"
                                        key={lv}
                                        onClick={() => update({ level: lv, parent_ids: lv === 1 ? [] : form.parent_ids })}
                                        className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors ${
                                            form.level === lv
                                                ? "border-primary bg-secondary text-primary"
                                                : "border-border bg-card text-muted-foreground hover:border-primary/30"
                                        }`}
                                        data-testid={`category-level-${lv}-button`}
                                    >
                                        {t(`categories.levels.l${lv}`)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Parents (L2 only) */}
                    {isL2 && (
                        <div>
                            <Label>
                                <span className="me-1">{t("categories.fields.parents")}</span>
                                <span className="text-destructive">*</span>{" "}
                                <span className="text-xs text-muted-foreground">({form.parent_ids.length})</span>
                            </Label>
                            <div className="mt-1.5 rounded-xl border border-border bg-card overflow-hidden">
                                <div className="relative border-b border-border">
                                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                                    <input
                                        type="text"
                                        value={parentSearch}
                                        onChange={(e) => setParentSearch(e.target.value)}
                                        placeholder={t("categories.parentSearchPlaceholder")}
                                        className="w-full ps-9 pe-3 py-2 text-xs bg-transparent outline-none"
                                        data-testid="category-parent-search"
                                    />
                                </div>
                                <div className="max-h-56 overflow-y-auto">
                                    {filteredParents.length === 0 && (
                                        <p className="text-xs text-muted-foreground px-3 py-3">
                                            {(tree || []).length === 0 ? t("categories.noL1") : t("categories.noResults")}
                                        </p>
                                    )}
                                    {filteredParents.map((p) => {
                                        const checked = form.parent_ids.includes(p.id);
                                        return (
                                            <label
                                                key={p.id}
                                                className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-secondary/50 ${checked ? "bg-secondary/30" : ""}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleParent(p.id)}
                                                    className="rounded border-border"
                                                    data-testid={`category-parent-${p.id}`}
                                                />
                                                <span className="flex-1 min-w-0">
                                                    <span className="text-sm">{p.name_en}</span>
                                                    <span className="text-xs text-muted-foreground ms-2">{p.name_ar}</span>
                                                </span>
                                                <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0">
                                                    <Badge variant="outline" className="text-[10px]">
                                                        {p.childCount} {t("categories.subs")}
                                                    </Badge>
                                                    {!p.is_active && (
                                                        <Badge variant="secondary" className="text-[10px]">
                                                            {t("categories.statusInactiveShort")}
                                                        </Badge>
                                                    )}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                            {touched.parent_ids && validation.parent_ids && (
                                <p className="mt-1 text-xs text-destructive">{validation.parent_ids}</p>
                            )}
                        </div>
                    )}

                    {/* Names */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FieldName
                            id="name_en" label={t("categories.fields.nameEn")} required
                            dir="ltr" value={form.name_en} max={NAME_MAX}
                            onChange={(v) => update({ name_en: v })}
                            onBlur={() => markTouched("name_en")}
                            error={touched.name_en && validation.name_en}
                            inputRef={firstInputRef}
                            testId="category-name-en"
                        />
                        <FieldName
                            id="name_ar" label={t("categories.fields.nameAr")} required
                            dir="rtl" lang="ar" value={form.name_ar} max={NAME_MAX}
                            onChange={(v) => update({ name_ar: v })}
                            onBlur={() => markTouched("name_ar")}
                            error={touched.name_ar && validation.name_ar}
                            className="font-arabic"
                            testId="category-name-ar"
                        />
                    </div>

                    {/* Explanatory subtitles — EN + AR */}
                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="expl_en">{t("categories.fields.explanatoryEn")}</Label>
                            <Textarea
                                id="expl_en"
                                dir="ltr"
                                rows={2}
                                value={form.explanatory_name_en || ""}
                                onChange={(e) => update({ explanatory_name_en: e.target.value })}
                                onBlur={() => markTouched("explanatory_name_en")}
                                placeholder={t("categories.fields.explanatoryEnPlaceholder")}
                                className="mt-1.5"
                                data-testid="category-expl-en"
                                maxLength={EXPL_MAX + 50}
                            />
                            <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                                <span>{t("categories.fields.explanatoryEnHint")}</span>
                                <span className={form.explanatory_name_en && form.explanatory_name_en.length > EXPL_MAX ? "text-destructive" : ""}>
                                    {form.explanatory_name_en?.length || 0} / {EXPL_MAX}
                                </span>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="expl_ar">{t("categories.fields.explanatoryAr")}</Label>
                            <Textarea
                                id="expl_ar"
                                dir="rtl"
                                lang="ar"
                                rows={2}
                                value={form.explanatory_name_ar || ""}
                                onChange={(e) => update({ explanatory_name_ar: e.target.value })}
                                onBlur={() => markTouched("explanatory_name_ar")}
                                className="mt-1.5 font-arabic"
                                data-testid="category-expl-ar"
                                maxLength={EXPL_MAX + 50}
                            />
                            <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                                <span>{t("categories.fields.explanatoryArHint")}</span>
                                <span className={form.explanatory_name_ar && form.explanatory_name_ar.length > EXPL_MAX ? "text-destructive" : ""}>
                                    {form.explanatory_name_ar?.length || 0} / {EXPL_MAX}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="flex items-center gap-1.5">
                                <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                                {t("categories.fields.tagsEn")}
                            </Label>
                            <div className="mt-1.5">
                                <TagsInput
                                    value={form.tags_en}
                                    onChange={(v) => update({ tags_en: v })}
                                    placeholder={t("categories.fields.tagsEnPlaceholder")}
                                    dir="ltr"
                                    testId="category-tags-en"
                                />
                            </div>
                            <TagSuggestions
                                pool={allTagsEn}
                                used={form.tags_en}
                                onPick={(tg) => update({ tags_en: [...form.tags_en, tg] })}
                            />
                        </div>
                        <div>
                            <Label className="flex items-center gap-1.5">
                                <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                                {t("categories.fields.tagsAr")}
                            </Label>
                            <div className="mt-1.5">
                                <TagsInput
                                    value={form.tags_ar}
                                    onChange={(v) => update({ tags_ar: v })}
                                    placeholder={t("categories.fields.tagsArPlaceholder")}
                                    dir="rtl"
                                    testId="category-tags-ar"
                                />
                            </div>
                            <TagSuggestions
                                pool={allTagsAr}
                                used={form.tags_ar}
                                onPick={(tg) => update({ tags_ar: [...form.tags_ar, tg] })}
                                rtl
                            />
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground -mt-2">
                        {t("categories.fields.tagsHint")}
                    </p>

                    {/* Active toggle */}
                    <div className="rounded-xl border border-border p-3">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <Label className="cursor-pointer">{t("categories.fields.active")}</Label>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    {form.is_active
                                        ? t("categories.statusActive")
                                        : t("categories.statusInactive")}
                                </p>
                            </div>
                            <Switch
                                checked={form.is_active}
                                onCheckedChange={(v) => update({ is_active: v })}
                                data-testid="category-active-switch"
                            />
                        </div>
                        {willHide && (
                            <div className="mt-3 rounded-lg border border-warning/40 bg-warning/5 p-2.5 flex gap-2">
                                <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                                <p className="text-xs">
                                    {t("categories.deactivateWarning", {
                                        providers: myUsage?.providers || 0,
                                        offers: myUsage?.offers || 0,
                                    })}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Member preview */}
                    {(form.name_en || form.name_ar) && (
                        <div className="rounded-xl border border-dashed border-border bg-muted/30 px-4 py-3">
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">
                                {t("categories.previewLabel")}
                            </p>
                            <div className="flex items-center gap-2 text-sm">
                                <Sparkles className="h-4 w-4 text-primary" />
                                <span className="font-semibold">
                                    {lang === "ar" ? (form.name_ar || form.name_en) : (form.name_en || form.name_ar)}
                                </span>
                                {form.name_en && form.name_ar && (
                                    <span className="text-xs text-muted-foreground">
                                        · {lang === "ar" ? form.name_en : form.name_ar}
                                    </span>
                                )}
                                {!form.is_active && (
                                    <Badge variant="secondary" className="text-[10px]">{t("categories.statusInactiveShort")}</Badge>
                                )}
                            </div>
                            {(lang === "ar" ? form.explanatory_name_ar : form.explanatory_name_en) && (
                                <p className={`mt-1 text-xs text-muted-foreground ${lang === "ar" ? "font-arabic" : ""}`} dir={lang === "ar" ? "rtl" : "ltr"}>
                                    {lang === "ar" ? form.explanatory_name_ar : form.explanatory_name_en}
                                </p>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                            {error}
                        </div>
                    )}
                </form>

                {/* Footer */}
                <div className="border-t border-border px-6 py-4 bg-background">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => requestClose()}
                            data-testid="category-cancel-button"
                        >
                            {t("common.cancel")}
                        </Button>
                        <div className="flex items-center gap-2 flex-wrap">
                            {!isEdit && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={submitting || !isValid}
                                    onClick={() => submit(null, "save_add_another")}
                                    data-testid="category-save-add-another"
                                >
                                    {submitting && submitMode === "save_add_another" && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                                    {t("categories.saveAndAddAnother")}
                                </Button>
                            )}
                            {!isEdit && form.level === 1 && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={submitting || !isValid}
                                    onClick={() => submit(null, "save_add_l2")}
                                    data-testid="category-save-add-l2"
                                    title={t("categories.saveAndAddL2Hint")}
                                >
                                    {submitting && submitMode === "save_add_l2" && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                                    <Plus className="h-3.5 w-3.5 me-1" />
                                    {t("categories.saveAndAddL2")}
                                </Button>
                            )}
                            <Button
                                type="button"
                                disabled={submitting || !isValid || (isEdit && !dirty)}
                                onClick={() => submit(null, "save")}
                                data-testid="category-save-button"
                            >
                                {submitting && submitMode === "save" && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                                {saveLabel}
                            </Button>
                        </div>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>{t("categories.shortcutHint")}</span>
                        {dirty && <span className="text-warning inline-flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-warning" /> {t("categories.unsavedChanges")}
                        </span>}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

function FieldName({ id, label, required, dir, lang: fieldLang, value, max, onChange, onBlur, error, className, inputRef, testId }) {
    const over = value && value.length > max;
    return (
        <div>
            <Label htmlFor={id}>
                {label}{required && <span className="text-destructive ms-0.5">*</span>}
            </Label>
            <Input
                id={id}
                ref={inputRef}
                dir={dir}
                lang={fieldLang}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                className={`mt-1.5 ${className || ""} ${error ? "border-destructive focus-visible:ring-destructive/30" : ""}`}
                maxLength={max + 20}
                data-testid={testId}
            />
            <div className="mt-1 flex items-center justify-between text-xs">
                <span className={error ? "text-destructive" : "text-transparent"}>{error || "·"}</span>
                <span className={over ? "text-destructive" : "text-muted-foreground"}>{value.length} / {max}</span>
            </div>
        </div>
    );
}

function TranslationChip({ ok_en, ok_ar, t }) {
    const both = ok_en && ok_ar;
    return (
        <div className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-semibold border ${both ? "border-success/40 text-success bg-success/5" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
            {both ? <CheckCircle2 className="h-3 w-3" /> : <Languages className="h-3 w-3" />}
            <span className={ok_en ? "" : "line-through opacity-70"}>EN</span>
            <span className="opacity-50">·</span>
            <span className={ok_ar ? "" : "line-through opacity-70"}>AR</span>
        </div>
    );
}

function TagSuggestions({ pool, used, onPick, rtl }) {
    const usedSet = new Set((used || []).map((t) => t.toLowerCase()));
    const suggestions = (pool || []).filter((t) => !usedSet.has(t.toLowerCase())).slice(0, 6);
    if (suggestions.length === 0) return null;
    return (
        <div className="mt-1.5 flex flex-wrap gap-1" dir={rtl ? "rtl" : "ltr"}>
            {suggestions.map((tg) => (
                <button
                    type="button"
                    key={tg}
                    onClick={() => onPick(tg)}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                >
                    <Plus className="h-2.5 w-2.5" />
                    {tg}
                </button>
            ))}
        </div>
    );
}

/* Re-export PolyHierarchyBadge for backward compat with any older imports */
export function PolyHierarchyBadge({ count }) {
    if (count <= 1) return null;
    return (
        <Badge variant="secondary" className="bg-primary-tint text-primary text-[10px] uppercase tracking-wide">
            {`Linked × ${count}`}
        </Badge>
    );
}
